<template>
  <div class="s-footer-bottom">
    <SAppContainer class="s-footer-bottom__content">
      <span class="s-footer-bottom__copyright">
        &copy; {{ copyrightYear }} Solvari.
        {{ tr("main_site.layout.footer_rights_reserved") }}
      </span>

      <ul class="s-footer-bottom__links">
        <li v-if="showReviewPolicy">
          <component
            :is="linkTag"
            class="s-footer-bottom__link"
            v-bind="
              linkProps(ziggyUnsafe('client.about.terms_of_use_reviews'), true)
            "
          >
            {{ tr("main_site.layout.footer_menu_solvari.review_policy") }}
          </component>
        </li>
        <li>
          <button
            class="s-footer-bottom__link"
            data-cookie-popup="btn-open"
            type="button"
            @click="cookiePopup.visible.value = true"
          >
            {{ tr("main_site.layout.footer_cookies") }}
          </button>
        </li>
        <li>
          <component
            :is="linkTag"
            class="s-footer-bottom__link"
            v-bind="linkProps(ziggyUnsafe('client.about.terms_of_use'), true)"
          >
            {{ tr("main_site.layout.footer_conditions") }}
          </component>
        </li>
        <li>
          <component
            :is="linkTag"
            class="s-footer-bottom__link"
            v-bind="linkProps(ziggyUnsafe('client.about.disclaimer'), true)"
          >
            {{ tr("main_site.layout.footer_disclaimer") }}
          </component>
        </li>
        <li>
          <component
            :is="linkTag"
            class="s-footer-bottom__link"
            v-bind="linkProps(ziggyUnsafe('client.about.privacy'), true)"
          >
            {{ tr("main_site.layout.footer_privacy") }}
          </component>
        </li>
      </ul>
    </SAppContainer>
  </div>
</template>

<script setup lang="ts">
import type { Component } from "vue";

import { computed } from "vue";

import { ziggyUnsafe } from "@solvari/common-fe/integrations";
import { useI18nMainSiteLayout } from "@solvari/translations";
import { getEnv } from "@solvari/utils";

import SAppContainer from "@/atoms/SAppContainer.vue";
import { cookiePopupState } from "@/organisms/cookie-popup/useCookiePopup.ts";

// Not dayjs: the footer is in every layout, so importing it here makes 22 kB eager.
const copyrightYear = new Date().getFullYear();

const props = defineProps<{ linkComponent?: Component }>();

const { tr } = useI18nMainSiteLayout();
const showReviewPolicy = getEnv().config.localeCode === "be-NL";
const cookiePopup = cookiePopupState();

const linkTag = computed(() => props.linkComponent ?? "a");
function linkProps(url: string, external = false) {
  return props.linkComponent ? { to: url, external } : { href: url };
}
</script>

<style lang="postcss">
.s-footer-bottom {
  @apply bg-primary-700 py-6;

  &__content {
    @apply flex flex-col-reverse items-center gap-4;
  }

  &__copyright {
    @apply text-base font-medium text-white;
  }

  &__links {
    @apply m-0 flex list-none flex-wrap items-center justify-center p-0 gap-2;
  }

  &__link {
    @apply flex h-10 cursor-pointer items-center rounded-md px-3 py-2 text-base font-medium text-slate-300 no-underline;

    &:hover {
      @apply text-white underline;
    }
  }

  @screen 1000 {
    &__content {
      @apply flex-row justify-between;
    }

    &__links {
      @apply flex-nowrap;
    }
  }
}
</style>
